import React, { useContext, useEffect, useRef, useState, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import Table from 'components/Table'
import ItemsPerPageDropdown from 'components/ItemsPerPageDropdown'
import Pagination from 'features/Pagination'
import sortDirection from 'constants/sortDirection'
import { usePagination } from 'hooks/usePagination'
import { showErrorToast, showSuccessToast } from 'utils/toast'
import LoadingWrapper from 'components/LoadingWrapper'
import { CountriesContext } from 'providers/countries'

import {
  requestGetSensitiveSites,
  requestApproveSensitiveSite,
  requestRejectSensitiveSite
} from 'api/sensitiveSpecies'
import { makeSensitiveSitesColumns, makeSensitiveSitesRows } from './sensitiveSpeciesTable'
import {
  SensitiveSpeciesPageContainer,
  SensitiveSpeciesPageTitle,
  SensitiveSpeciesTableWrapper,
  SensitiveSpeciesFooterWrapper
} from './style'

const defaultPaginationState = {
  totalNumber: 0,
  resultsPerPage: 12,
  totalPages: 0,
  currentPage: 1,
  hasNext: true,
  hasPrevious: false
}

// Reassessment proposal purposes that require sensitive species handling
const REASSESSMENT_PURPOSE_VALUES = [3, 4, 5, 6]

let sitesFetchAbortController = null

const SENSITIVE_ACTIONS = {
  APPROVE: 'approve',
  REJECT: 'reject'
}

const getActionPayload = (siteItem) => {
  return {
    siteHistoryId: siteItem.historyId ?? siteItem.SitesHistory?.id ?? siteItem.id,
    viewerId: siteItem.viewerId,
    sensitiveSpeciesRequestType: siteItem.sensitiveSpeciesRequestType
  }
}

const isPayloadValid = ({ siteHistoryId, viewerId, sensitiveSpeciesRequestType }) => {
  return !(
    siteHistoryId == null ||
    siteHistoryId === '' ||
    viewerId == null ||
    viewerId === '' ||
    sensitiveSpeciesRequestType == null ||
    sensitiveSpeciesRequestType === ''
  )
}

const getDefaultFailureMessage = (actionType) => {
  if (actionType === SENSITIVE_ACTIONS.APPROVE) {
    return (
      <FormattedMessage id="failedToApproveSensitiveSite" defaultMessage="Unable to approve this site. Please try again." />
    )
  }

  return (
    <FormattedMessage id="failedToRejectSensitiveSite" defaultMessage="Unable to reject this site. Please try again." />
  )
}

const getAlreadyProcessedSuccessMessage = (actionType) => {
  if (actionType === SENSITIVE_ACTIONS.APPROVE) {
    return (
      <FormattedMessage id="sensitiveSpeciesAlreadyApproved" defaultMessage="This site has already been approved." />
    )
  }

  return (
    <FormattedMessage id="sensitiveSpeciesAlreadyRejected" defaultMessage="This site has already been rejected." />
  )
}

const getActionSuccessMessage = (actionType) => {
  if (actionType === SENSITIVE_ACTIONS.APPROVE) {
    return (
      <FormattedMessage id="sensitiveSiteApproved" defaultMessage="Sensitive species site has been approved successfully." />
    )
  }

  return (
    <FormattedMessage id="sensitiveSiteRejected" defaultMessage="Sensitive species site has been rejected." />
  )
}

const getServerErrorMessage = (result) => {
  const serverMessage = result?.error ?? result?.message
  if (typeof serverMessage === 'string' && serverMessage) {
    return serverMessage
  }
  return null
}

const getCaughtErrorMessage = (error) => {
  const message = error?.message ?? error?.response?.data?.message ?? error?.response?.data?.error
  if (typeof message === 'string' && message) {
    return message
  }
  return null
}

const SensitiveSpeciesManagement = () => {
  const { countriesOptions } = useContext(CountriesContext) || {}
  const { pagination, setPagination, selectPage, nextPage, prevPage, nextSection, prevSection, changeResultsPerPage } =
    usePagination(defaultPaginationState)

  const filterTaxonIdRef = useRef(null)

  const [sorting, setSorting] = useState({
    sortBy: 'siteCode',
    sortDirection: sortDirection.ASC
  })

  const [sites, setSites] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [actionInProgressId, setActionInProgressId] = useState(null)

  function getSensitiveSites() {

    if (sitesFetchAbortController) {
      setIsLoading(false)
      sitesFetchAbortController.abort()
    }

    sitesFetchAbortController = new AbortController()

    const params = {
      resultsPerPage: pagination.resultsPerPage,
      currentPage: pagination.currentPage,
      sortBy: sorting.sortBy,
      sortDirection: sorting.sortDirection,
      proposalPurpose: REASSESSMENT_PURPOSE_VALUES,
      ...(filterTaxonIdRef.current != null && filterTaxonIdRef.current !== ''
        ? { taxonId: filterTaxonIdRef.current }
        : {}),
      signal: sitesFetchAbortController.signal
    }

    setIsLoading(true)

    requestGetSensitiveSites(params)
      .then((data) => {
        if (data?.error) {
          showErrorToast(
            typeof data.error === 'string'
              ? data.error
              : (
                <FormattedMessage
                  id="failedToLoadSensitiveSites"
                  defaultMessage="Unable to load sensitive sites. Please try again."
                />
              ),
            'showBorder'
          )
          setSites([])
          setPagination(defaultPaginationState)
          setIsLoading(false)
          return
        }

        setIsLoading(false)
        const sitesData =
          data?.sites || data?.sensitiveSites || (Array.isArray(data) ? data : data?.data) || []

        const filteredSites = (sitesData || []).filter((item) => {
          const siteHistory = item.SitesHistory || item

          // Always include sites that have a sensitive-species visibility request,
          // regardless of proposalPurpose. Backend already enforces visibility.
          const hasVisibilityRequest =
            siteHistory?.sensitiveSpeciesVisibilityRequest ??
            siteHistory?.sensitive_species_visibility_request ??
            item.sensitiveSpeciesVisibilityRequest ??
            item.sensitive_species_visibility_request

          if (hasVisibilityRequest === true) {
            return true
          }

          const proposalPurpose = siteHistory?.proposalPurpose ?? item.proposalPurpose
          if (proposalPurpose === null || proposalPurpose === undefined) {
            return true
          }
          return REASSESSMENT_PURPOSE_VALUES.includes(Number(proposalPurpose))
        })

        setSites(filteredSites)
        const paginationData = data?.pagination || defaultPaginationState
        setPagination(paginationData)
      })
      .catch((err) => {
        setIsLoading(false)
        if (err.name === 'AbortError') {
          return
        }
        showErrorToast(
          <FormattedMessage
            id="failedToLoadSensitiveSites"
            defaultMessage="Unable to load sensitive sites. Please try again."
          />,
          'showBorder'
        )
      })
  }

  const handleSensitiveSpeciesDataChanged = (event) => {
    const detail = event?.detail
    if (detail?.taxonId != null) {
      filterTaxonIdRef.current = detail.taxonId
    }
    getSensitiveSites()
  }

  useEffect(() => {
    getSensitiveSites()

    globalThis.addEventListener('sensitiveSpeciesDataChanged', handleSensitiveSpeciesDataChanged)
    return () => {
      globalThis.removeEventListener('sensitiveSpeciesDataChanged', handleSensitiveSpeciesDataChanged)
    }
  }, [
    pagination.resultsPerPage,
    pagination.currentPage,
    sorting.sortBy,
    sorting.sortDirection
  ])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') getSensitiveSites()
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [getSensitiveSites])

  const isAlreadyProcessedResponse = (result, forApprove) => {
    if (!result) return false
    const alreadyFlag =
      result.alreadyProcessed === true ||
      (forApprove ? result.alreadyApproved === true : result.alreadyRejected === true)
    if (alreadyFlag) return true
    const msg = typeof result.message === 'string' ? result.message : ''
    const err = typeof result.error === 'string' ? result.error : ''
    const text = `${msg} ${err}`.toLowerCase()
    return text.length > 0 && text.includes('already')
  }

  const handleApprove = async (siteItem) => {
    await handleSensitiveAction(siteItem, SENSITIVE_ACTIONS.APPROVE)
  }

  const handleReject = async (siteItem) => {
    await handleSensitiveAction(siteItem, SENSITIVE_ACTIONS.REJECT)
  }

  const handleSensitiveAction = async (siteItem, actionType) => {
    const payload = getActionPayload(siteItem)
    const forApprove = actionType === SENSITIVE_ACTIONS.APPROVE

    if (!isPayloadValid(payload)) {
      showErrorToast(getDefaultFailureMessage(actionType), 'showBorder')
      return
    }

    if (actionInProgressId != null) return
    setActionInProgressId(payload.viewerId)

    try {
      const requestFn = forApprove ? requestApproveSensitiveSite : requestRejectSensitiveSite
      const result = await requestFn(payload.siteHistoryId, payload.viewerId, payload.sensitiveSpeciesRequestType)

      if (isAlreadyProcessedResponse(result, forApprove)) {
        showSuccessToast(getAlreadyProcessedSuccessMessage(actionType))
        getSensitiveSites()
        return
      }

      const isError =
        result?.error != null ||
        result?.success === false ||
        (typeof result?.message === 'string' && result.message.toLowerCase().includes('error'))
      if (isError) {
        showErrorToast(getServerErrorMessage(result) ?? getDefaultFailureMessage(actionType), 'showBorder')
        return
      }

      showSuccessToast(getActionSuccessMessage(actionType))
      getSensitiveSites()
    } catch (error) {
      if (error?.name === 'AbortError') return

      const message = getCaughtErrorMessage(error)
      const messageStr = typeof message === 'string' ? message.toLowerCase() : ''
      if (messageStr.includes('already')) {
        showSuccessToast(getAlreadyProcessedSuccessMessage(actionType))
        getSensitiveSites()
        return
      }

      showErrorToast(message ?? getDefaultFailureMessage(actionType), 'showBorder')
    } finally {
      setActionInProgressId(null)
    }
  }

  const columns = useMemo(() => makeSensitiveSitesColumns(sorting, setSorting), [sorting])
  const rows = useMemo(
    () => makeSensitiveSitesRows(sites, handleApprove, handleReject, countriesOptions, actionInProgressId),
    [sites, handleApprove, handleReject, countriesOptions, actionInProgressId]
  )

  return (
      <SensitiveSpeciesPageContainer>
        <SensitiveSpeciesPageTitle data-test="legend">
          <FormattedMessage id="sensitiveSpecies" defaultMessage="Sensitive site approval" />
        </SensitiveSpeciesPageTitle>
        <SensitiveSpeciesTableWrapper>
          <LoadingWrapper size={'24px'} isLoading={isLoading}>
            <Table
              rows={rows}
              columns={columns}
              noResultMessage="noSensitiveSitesFound"
              dataTest="sensitive-sites-list"
              clearFilters={() => { }}
              isLoading={isLoading}
            />
          </LoadingWrapper>
          <SensitiveSpeciesFooterWrapper data-test="pagination">
            <ItemsPerPageDropdown
              currentPage={pagination.currentPage}
              resultsPerPage={pagination.resultsPerPage}
              totalNumber={pagination.totalNumber}
              onChange={changeResultsPerPage}
              dataTest="sensitive-sites-items-per-page"
              menuPlacement="top"
            />
            {pagination.totalPages > 1 && (
              <Pagination
                activePages={pagination.currentPage}
                numberOfPages={pagination.totalPages}
                nextPage={nextPage}
                prevPage={prevPage}
                nextSection={nextSection}
                prevSection={prevSection}
                selectPage={selectPage}
                dataTest="page-number"
              />
            )}
          </SensitiveSpeciesFooterWrapper>
        </SensitiveSpeciesTableWrapper>
      </SensitiveSpeciesPageContainer>
  )
}

export default SensitiveSpeciesManagement

